import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import { clients } from '../../../util/clients';
import { printerHandler } from '../../../util/pdf-to-printer.util';
import { toIsoDate, toShortDate } from '../../../util/shared/tmc-global';
import {
  amountFormat, amountToNumber,
  sanitizeZipCode,
} from '../../../util/shared/vue-global';
import { getWorkstationNumber } from '../../../util/workstation-util';
import { generateQueryString } from '../../common/base/report/report-util';
import { getTaxYearStatus, isProtestRefundOrCredit } from './tax-common.util';
import { mapForte2CardTypeToSpecialPaymentTypeKey } from '../../../util/processCreditCard/forte2.util';
import { buildManualCCRecord, hasUnchargedProcessedCredit, normalizeSpecialPaymentType } from './buildManualCCRecord';

const { backendRest } = clients.direct;
export default {
  computed: {
    ...mapState({
      nextPaymentNumber: (state) => state.tax.taxPayment.nextPaymentNumber,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem,
      halfOrPostmark: (state) => state.tax.taxPayment.halfOrPostmark,
      paymentShotCutStore: (state) => state.tax.taxPayment.paymentShotCutStore,
      payrixPayment: (state) => state.common.payrixPayment,
      payrixSuccessFlag: (state) => state.common.payrixPayment.paymentSuccessFlag,
      forteResponse: (state) => state.ForteService,
      isVisiblePIcklistDialog: (state) => state.base.crud.isVisible.pickListMakePaymentDialog,
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
      forte2Payment: (state) => state.common.forte2Payment,
      forte2SuccessFlag: (state) => state.common.forte2Payment.paymentSuccessFlag,
    }),
    ...mapGetters(['todaysDate']),
    ...mapGetters('base/crud', ['item', 'isVisible', 'criteria']),
    ...mapGetters('auth/token', ['authUser']),
    ...mapGetters('common/payrixPayment', ['getLastSavedPaymentItem', 'getCapturedCCPayments']),
    // DOUBLE-SUBMISSION GUARD: what the Save / Save & print buttons bind their :disabled to.
    // `saveInFlight` is owned end-to-end by upsertTransaction (set synchronously on entry,
    // cleared in its finally), so unlike `disableSave` — which several branches inside
    // postUpsertTransaction legitimately flip back to false part-way through to clear the
    // spinner — it can never drop to false while an upsertTaxPayment is still in flight.
    // Both are OR-ed so the existing spinner behavior is unchanged.
    isSaveBlocked() {
      return this.disableSave || this.saveInFlight;
    },
  },
  data() {
    return {
      auditRecord: null,
      paymentNumberopolling: null,
      paymentReRenderFlag: false,
      disableSave: false,
      // True from the first click until the whole save (payment + CC records + receipt)
      // settles. Read by the button bindings and by the user-initiated save handlers.
      saveInFlight: false,
      saveInFlightCount: 0,
      mobileHomeArr: [],
    };
  },
  methods: {
    ...mapActions(['reAuth', 'flashError']),
    ...mapMutations('base/crud', ['setIsVisible', 'setItem', 'setItems']),
    ...mapActions('tax/taxPayment', [
      'upsertTaxPayment',
      'upsertTaxPaymentAudit',
      'upsertTaxPaymentAudits',
      // 'generateNextPaymentNumber',
      'callMakeGLobalPaymentNumberUndefined',
      'loadPaymentsWkstList',
      // 'loadMoreTax',
    ]),
    ...mapActions('tax/ccPayments', [
      'upsertCCPaymentsTrans',
    ]),
    ...mapActions('common/payrixPayment', [
      'setPaymentSuccessFlag',
      'setLastSavedPaymentItem',
    ]),
    ...mapActions('common/forte2Payment', [
      'resetForte2Payment',
      'clearCapturedForte2Payments',
    ]),
    ...mapMutations('ForteService', [
      'setFortePaymentSuccessFlag',
      'clearCapturedForteResponses',
    ]),
    ...mapMutations('tax/taxPayment', [
      'setHalfOrPostmark',
      'setPaymentShotCutStore',
      'setDisableOverUnderPay',
      'setIsThereAnyUnpaids',
      'setMisMatchItemsList',
    ]),
    ...mapMutations('tax/taxInquiryTabs', [
      'setEmptyPickListFlag',
    ]),
    ...mapMutations('tax/taxPayment', [
      'setUpsertItem',
    ]),
    ...mapActions('tax/taxInquires', [
      'upsertDecalDateAndDecalNumber',
    ]),
    // Guard for USER-INITIATED saves: the Save / Save & print buttons, the payment dialog's
    // Enter key and the CRUD-list save. Returns true (and logs) when a save is already
    // running, in which case the handler must ignore this click - the first call is already
    // creating the payment, and letting the second through is what produces a second
    // payment record. Needed ON TOP OF the :disabled binding because the Enter-key and
    // dialog-emit paths never touch the button at all, and because :disabled only takes
    // effect once Vue has flushed a re-render.
    // Deliberately NOT applied to the credit-card success watchers, which call
    // preUpsertTransaction/upsertTransaction directly: a charge that has already hit the
    // card must never be silently dropped.
    isDuplicateSaveClick() {
      if (!this.saveInFlight) return false;
      console.warn('[Duplicate save guard] Save ignored - a save for this payment is already in flight.');
      return true;
    },
    // Same guard, for the template-bound paths that hand their args straight to
    // preUpsertTransaction (dialog Enter, CRUD-list save, PaymentButtons). Args are
    // forwarded verbatim so behavior is otherwise identical to binding preUpsertTransaction.
    async guardedPreUpsert(...upsertArgs) {
      if (this.isDuplicateSaveClick()) return undefined;
      return this.preUpsertTransaction(...upsertArgs);
    },
    // DOUBLE-SUBMISSION GUARD. Every Save / Save & print path on every payment screen
    // funnels through here, so this is the one place that knows a save is running.
    // `saveInFlight` is raised SYNCHRONOUSLY — before the first await and before the
    // does-not-balance window.confirm — so a second click cannot slip in behind it, and it
    // is lowered only in the finally, after the payment, its CC records and its receipt have
    // all settled. Counted rather than a plain boolean because a CC success watcher can fire
    // its own save while a manual one is still running; a bare `= false` in the inner
    // finally would unblock the buttons while the outer save was still in flight.
    // Without this, two concurrent upsertTaxPayment calls that both carry no paymentNumber
    // each mint their own number from getNextPaymentNumber() and write SEPARATE payment
    // records - the duplicate-payment bug seen at Murray when the card is run on a
    // standalone Forte terminal (no callback, so the form is still fully populated).
    async upsertTransaction(...upsertArgs) {
      // `|| 0` so a hand-rolled test context without the data() defaults still counts.
      this.saveInFlightCount = (this.saveInFlightCount || 0) + 1;
      this.saveInFlight = true;
      try {
        return await this.runUpsertTransaction(...upsertArgs);
      } finally {
        this.saveInFlightCount = Math.max(0, (this.saveInFlightCount || 0) - 1);
        if (!this.saveInFlightCount) {
          this.saveInFlight = false;
          // Also clear `disableSave` here so the buttons come back on EVERY exit, including
          // the two paths that previously left it stuck true for the life of the dialog: an
          // upsertTaxPayment that throws, and postUpsertTransaction's missing-workstation
          // early return. Nothing outside this save funnel raises `disableSave`, so owning
          // the reset here is safe.
          this.disableSave = false;
        }
      }
    },
    // eslint-disable-next-line max-len
    async runUpsertTransaction(item, avoidUpdatingCrudList = true, isItFromSaveNPrint = false, shouldCloseDialog = true) {
      const { updatedItem, totalDue, totalPayment } = this.upsertNOverPaymentsCommon(item);
      const filUpdatedItem = (updatedItem.paymentDetails || []).filter((ft) => (!!ft.amount));
      if (!(updatedItem.taxDetails.length) && !(filUpdatedItem.length)) {
        this.onPaymentDialogClose();
        return {};
      }
      const isThereACashPayment = (item.paymentDetails || []).filter((ft) => ft.paymentType === 'CASH');
      const isThereACheckPayment = (item.paymentDetails || []).filter((ft) => ft.paymentType === 'CHECK');
      const isThereATaxPaymentType = (item.taxDetails || []).filter((ft) => ft.taxType === 'TAXPAYMENT');
      let result = {};
      if (amountToNumber(totalDue) === amountToNumber(totalPayment)) {
        // if the totalAmt and totalPaymentAmt are equal
        result = await this.postUpsertTransaction(item, avoidUpdatingCrudList,
          updatedItem, totalDue, totalPayment, isItFromSaveNPrint, shouldCloseDialog);
        result.resetTheDialog = true;
      } else if ((amountToNumber(totalPayment) > amountToNumber(totalDue))
        && (isThereACashPayment.length
          || (this.paymentSettingItem.allowChangeDueOnCheckPayments && isThereACheckPayment.length))) {
        // if the totalPaymentAmt > totalAmt and there is a cash payment
        // (or a check payment when change-due-on-check is allowed)
        result = await this.postUpsertTransaction(item, avoidUpdatingCrudList,
          updatedItem, totalDue, totalPayment, isItFromSaveNPrint, shouldCloseDialog);
        result.resetTheDialog = true;
      } else if (!isThereATaxPaymentType.length) { // if there is no taxType === TAXPAYMENT
        await this.postUpsertTransaction(item, avoidUpdatingCrudList,
          updatedItem, totalDue, totalPayment, isItFromSaveNPrint, shouldCloseDialog);
        result.resetTheDialog = true;
      } else if (!shouldCloseDialog) { // when doing file-upload
        result = await this.postUpsertTransaction(item, avoidUpdatingCrudList,
          updatedItem, totalDue, totalPayment, isItFromSaveNPrint, shouldCloseDialog);
        result.resetTheDialog = true;
      } else {
        // Show confirmation dialog
        const confirmResult = window.confirm('Warning - The total of payments on this transaction does not equal the total amount due. This needs to be fixed before posting.');
        if (confirmResult) {
          // User clicked OK
          result = await this.postUpsertTransaction(item, avoidUpdatingCrudList,
            updatedItem, totalDue, totalPayment, isItFromSaveNPrint, shouldCloseDialog);
          result.resetTheDialog = true;
        } else {
          // User clicked Cancel - return the original item to prevent data loss
          result = { resetTheDialog: false, item };
        }
      }
      return result;
    },
    validateUpsertFields(item) {
      const allowedFields = [
        '_id',
        'fiscalYear',
        'payDate',
        'paymentNumber',
        'workstation',
        'void',
        'taxDetails',
        'paymentDetails',
        'overUnderStatus',
        'misMatchFlag',
        'loadMoreTax',
        'isExempt',
        'curWkst',
        'userName',
        'isFirstTimePrintReceipt',
        'mail',
        'ccProcessed',
      ];

      const allowedTaxDetailsFields = [
        '_id',
        'taxYear',
        'taxId',
        'schoolDistrict',
        'taxAmt',
        'penaltyAmt',
        'mailFee',
        'lienFee',
        'advFee',
        'otherFee',
        'total',
        'feeNumber',
        'void',
        'isItNewVoidedRecord',
        'isItFromPaymentTaxDetails',
        'itsNewRecord',
        'protest',
        'posted',
        'totalDue',
        'reciept',
        'taxType',
        'fees',
        'statusCode',
        'totalFees',
        'grossAssessed',
        'exemptions',
        'ownerName',
      ];

      const allowedPaymentDetailsFields = [
        '_id',
        'amount',
        'paidBy',
        'checkNumber',
        'paymentType',
        'paymentsModes',
        'eftType',
        'creditType',
        'isReconcilled',
        'mtgCode',
        'ccProcessed',
        'overpaymentAmt',
      ];

      // Filter top-level fields
      const filteredItem = {};
      Object.keys(item).forEach((key) => {
        if (allowedFields.includes(key)) {
          filteredItem[key] = item[key];
        }
      });

      // Filter taxDetails array
      if (Array.isArray(filteredItem.taxDetails)) {
        filteredItem.taxDetails = filteredItem.taxDetails.reduce((acc, taxDetail) => {
          if (taxDetail.taxId !== null && taxDetail.taxId !== undefined) {
            const filteredTaxDetail = {};
            Object.keys(taxDetail).forEach((key) => {
              if (allowedTaxDetailsFields.includes(key)) {
                filteredTaxDetail[key] = taxDetail[key];
              }
            });
            acc.push(filteredTaxDetail);
          }
          return acc;
        }, []);
      }

      // Filter paymentDetails array
      if (Array.isArray(filteredItem.paymentDetails)) {
        filteredItem.paymentDetails = filteredItem.paymentDetails.map((paymentDetail) => {
          const filteredPaymentDetail = {};
          Object.keys(paymentDetail).forEach((key) => {
            if (allowedPaymentDetailsFields.includes(key)) {
              filteredPaymentDetail[key] = paymentDetail[key];
            }
          });
          return filteredPaymentDetail;
        });
      }

      return filteredItem;
    },
    async postUpsertTransaction(
      item, avoidUpdatingCrudList = true, updatedItem, totalDue, totalPayment,
      isItFromSaveNPrint = false, shouldCloseDialog,
    ) {
      this.disableSave = true;
      const uptItem = updatedItem;
      // filtering the only payment-details which has details
      const filterEmptyPayDet = (updatedItem.paymentDetails || []).filter((ft) => !!ft.paymentType
        && !!(amountToNumber(ft.amount)));
      uptItem.paymentDetails = filterEmptyPayDet;
      const filtedItems = (item.taxDetails || []).filter((el) => el.AIsVisible);
      /* For new item check workstation, and if it is not avaible exit early with message */
      if (!item.workstation) {
        this.flashError(
          'Error: While upserting receipt, workstation number not exist!',
        );
        return {};
      }
      if (isItFromSaveNPrint) {
        uptItem.isFirstTimePrintReceipt = isItFromSaveNPrint;
      }
      const { baseCrudKey } = this;
      const remainBal = amountFormat(totalPayment) - amountFormat(totalDue);
      const overUnderFeeLimit = amountToNumber(this.taxSettingItem.overUnderLimit);
      // 1. if there is a cash and there is a change, we dont require to create over/under
      // payment,if there is a no change then we need to create a over/under payment.
      // 2.if the payment record doesn't have payment type 'CASH',then the over/UndFee is applicable
      const checkIfThereIsACashPayment = (uptItem.paymentDetails || []).filter((ft) => ft.paymentType === 'CASH');
      const checkIfThereIsACheckPayment = (uptItem.paymentDetails || []).filter((ft) => ft.paymentType === 'CHECK');
      const diff = (checkIfThereIsACashPayment.length
        || (this.paymentSettingItem.allowChangeDueOnCheckPayments && checkIfThereIsACheckPayment.length))
        ? amountToNumber(totalDue) >= amountToNumber(totalPayment) : true;
      if ((remainBal !== 0) && (remainBal >= -(overUnderFeeLimit))
        && (remainBal <= overUnderFeeLimit) && diff) {
        uptItem.overUnderStatus = true;
      } else {
        uptItem.overUnderStatus = false;
      }
      const pickListCriteria = this.criteria('pickListMakePaymentDialog');
      const exemptFromCriteria = !!(pickListCriteria && pickListCriteria.enableExempt);
      const exemptFromDetails = uptItem.taxDetails.length > 0
        && uptItem.taxDetails.every((el) => el.isExempt === true);
      const isExempt = uptItem.loadMoreTax
        ? (exemptFromCriteria || uptItem.isExempt || false)
        : (exemptFromDetails || uptItem.isExempt || false);
      uptItem.isExempt = isExempt;
      for (let x = 0; x < (uptItem.taxDetails || []).length; x += 1) {
        delete uptItem.taxDetails[x].isExempt;
        if (uptItem.taxDetails[x]._id === undefined || uptItem.taxDetails[x]._id === null) {
          delete uptItem.taxDetails[x]._id;
        }
        if (uptItem.taxDetails[x].fiscalYear) {
          delete uptItem.taxDetails[x].fiscalYear;
        }
        if (uptItem.taxDetails[x].paymentNumber) {
          delete uptItem.taxDetails[x].paymentNumber;
        }
        if (uptItem.taxDetails[x].selected === true || uptItem.taxDetails[x].selected === false) {
          delete uptItem.taxDetails[x].selected;
        }
      }
      uptItem.curWkst = getWorkstationNumber();
      uptItem.userName = this.authUser;
      delete uptItem.auditRecordsForVoid;
      if (uptItem.paidBy) {
        delete uptItem.paidBy;
      }
      const isUnprocessed = (uptItem.paymentDetails || []).some(
        (p) => p.ccProcessed === false,
      );
      if (isUnprocessed && (this.forteResponse.fortePaymentSuscessFlag === false
        || this.payrixSuccessFlag === false)) {
        const proceed = window.confirm(
          'This transaction contains a credit card payment that has not been processed.\n\n'
          + 'Click "OK" to save anyway (it will be marked Unprocessed), or "Cancel" to stay here and process the card.',
        );
        if (!proceed) {
          this.disableSave = false;
          return {};
        }
      }
      delete uptItem.paidBy;
      if (uptItem.totalFees) {
        delete uptItem.totalFees;
      }
      if (uptItem.fees) {
        delete uptItem.fees;
      }

      const validatedItem = this.validateUpsertFields(uptItem);

      const returnedRes = await this.upsertTaxPayment({
        item: validatedItem,
        baseCrudKey,
        postProcessTransaction: this.postProccessData,
        avoidUpdatingCrudList,
      });
      // DUPLICATE-PAYMENT GUARD: carry the assigned identity back onto the dialog's bound
      // item so a re-save UPDATES this payment instead of creating a second record. Every
      // caller passes its own dialog object as `item` (this.item(<their crud key>)) BY
      // REFERENCE, so writing the number onto it in place fixes all callers regardless of
      // their crud key (paymentTransactionList/CommonPaymentDialog use baseCrudKey;
      // PickList uses baseCrudKeyFour; Protest uses its own key). Without this the dialog
      // keeps paymentNumber:0/null and a stalled/aborted/double-fired second save mints a
      // new payment (the statewide duplicate bug); the backend treats a provided
      // paymentNumber as an update. Vuex strict mode is off and in-place item mutation is
      // the existing pattern in this flow. Identity-only — no other fields touched.
      if (item && returnedRes && returnedRes.paymentNumber) {
        const dialogItem = item; // local alias (same ref) — matches the uptItem pattern above
        dialogItem.paymentNumber = returnedRes.paymentNumber;
        if (returnedRes._id) {
          dialogItem._id = returnedRes._id;
        }
      }
      if (this.mobileHomeArr.length) {
        this.setItems(['ComhDecalmmonDecalDialogCrudKey', this.mobileHomeArr]);
        setTimeout(() => {
          this.setIsVisible(['ComhDecalmmonDecalDialogCrudKey', true]);
        }, 1000);
      }
      if (this.odSettingItem.selectedGateway === 'payrix' && this.payrixSuccessFlag) {
        // Each charged card was captured as its own taxccpayments record (deduped by
        // transactionId) as it succeeded. The singleton lastSavedPaymentItem only holds the
        // LAST card, so save one CC record per captured card — otherwise a payment split
        // across multiple cards gets only one CC Payments Transaction row. Fall back to the
        // single lastSavedPaymentItem if nothing was captured (backward-compatible).
        const capturedCCPayments = this.getCapturedCCPayments || [];
        const ccRecordsToSave = capturedCCPayments.length
          ? capturedCCPayments
          : [this.getLastSavedPaymentItem].filter((it) => it && Object.keys(it).length > 0);
        let allCCSaved = ccRecordsToSave.length > 0;
        for (let i = 0; i < ccRecordsToSave.length; i += 1) {
          const ccRecord = { ...ccRecordsToSave[i], paymentNo: returnedRes.paymentNumber };
          try {
            // Sequential (not Promise.all): upsertCCPaymentsTrans does a read-modify-write of
            // the CC transaction crud list, so parallel writes would clobber each other's rows.
            // eslint-disable-next-line no-await-in-loop
            await this.upsertCCPaymentsTrans({
              item: ccRecord,
              baseCrudKey: 'ccPaymentTransaction',
            });
          } catch (ccPaymentError) {
            allCCSaved = false;
            // Log the failure with context
            console.error('Failed to save CC payment transaction for payment #', returnedRes.paymentNumber, {
              error: ccPaymentError,
              ccRecord,
            });
            // Show specific error that main payment succeeded but a CC record failed
            this.flashError(
              `Payment #${returnedRes.paymentNumber} was saved successfully, but a credit card transaction record failed to save. `
              + 'Please contact support to manually create the CC payment record.',
            );
          }
        }
        // Always clear the latched success flag so the next payment starts fresh.
        this.setPaymentSuccessFlag({ flag: false, transactionId: '' });
        // Only clear the captured records once they ALL persisted; keep them for manual
        // recovery if any upsert failed. setLastSavedPaymentItem({}) clears both the
        // singleton and the captured-records accumulator.
        if (allCCSaved) {
          this.setLastSavedPaymentItem({});
        }
      }
      if (this.odSettingItem.selectedGateway !== 'payrix' && this.forteResponse.fortePaymentSuscessFlag) {
        // Each charged card's raw Forte response was captured as it succeeded (deduped by
        // trace_number). ForteService.forteResponse only ever holds the LAST card, so build
        // one taxccpayments record per captured response — otherwise a payment split across
        // multiple cards gets only one CC Payments Transaction row. Falls back to the single
        // live response when nothing was captured (backward-compatible).
        const capturedForteResponses = this.forteResponse.capturedForteResponses || [];
        const forteResponsesToSave = capturedForteResponses.length
          ? capturedForteResponses
          : [this.forteResponse.forteResponse].filter((r) => r && Object.keys(r).length > 0);
        const fortePosReqs = forteResponsesToSave.map((resp) => this.fortePosReqMapping(
          uptItem, returnedRes.paymentNumber, this.forteResponse, resp,
        ));
        // taxDetails are the same for every card on the payment, so guarding the first
        // record covers them all.
        const fortePosReq = fortePosReqs.length ? fortePosReqs[0] : {};
        if (!fortePosReq.taxDetails || !fortePosReq.taxDetails.length) {
          console.error('[Forte Picklist] Receipt aborted: taxDetails is empty. uptItem:', JSON.stringify(uptItem));
          this.custumColrFlashMsg({ msg: 'Forte payment was processed but receipt generation failed (no tax details). Please contact support.', color: 'red' });
          this.setFortePaymentSuccessFlag(false);
          this.disableSave = false;
          return {};
        }
        // One full receipt page per charged card. Pairing each record with the response it
        // was built from matters: the ForteService singleton only holds the LAST card, so
        // reading it here would stamp every page with the last card's amount and trace #.
        const receiptPayments = fortePosReqs.map((posReq, i) => {
          const forteResp = forteResponsesToSave[i] || {};
          return {
            processedPayment: {
              transactionId: forteResp.trace_number,
              cardType: posReq.specialPaymentType,
              cardholderName: forteResp.name_on_card || posReq.paidBy,
              paidAmt: Number(forteResp.subtotal_amount) || 0,
              processingFee: Number(forteResp.service_fee_amount) || 0,
              taxPayerEmail: forteResp.billing_email_address || '',
            },
            taxDetails: posReq.taxDetails,
            paymentMethod: posReq.paymentType,
          };
        });
        try {
          const receiptResponse = await backendRest.post('/tax/ccPayments/forte/taxpayer-receipt', { payments: receiptPayments },
            { responseType: 'blob' });
          const blob = new Blob([receiptResponse.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        } catch (err) {
          console.error('[Forte Picklist] taxpayer-receipt POST failed:', (err && err.response && err.response.data) || err);
          this.custumColrFlashMsg({ msg: 'Forte payment was processed but receipt generation failed. Please contact support.', color: 'red' });
        } finally {
          this.setFortePaymentSuccessFlag(false);
          this.disableSave = false;
        }
        let allForteCCSaved = fortePosReqs.length > 0;
        for (let i = 0; i < fortePosReqs.length; i += 1) {
          try {
            // Sequential (not Promise.all): upsertCCPaymentsTrans does a read-modify-write of
            // the CC transaction crud list, so parallel writes would clobber each other's rows.
            // eslint-disable-next-line no-await-in-loop
            await this.upsertCCPaymentsTrans({
              item: fortePosReqs[i],
              baseCrudKey: 'ccPaymentTransaction',
            });
          } catch (err) {
            allForteCCSaved = false;
            console.error('[Forte Picklist] upsertCCPaymentsTrans failed:', err);
            this.custumColrFlashMsg({ msg: 'Forte payment processed and receipt generated, but a CC transaction record failed to save. Please contact support.', color: 'red' });
          }
        }
        // Only drop the captured responses once every record persisted; keep them for manual
        // recovery otherwise. Guarded on length so the single-response fallback path (and the
        // existing unit-test context) never calls the clear.
        if (allForteCCSaved && capturedForteResponses.length) {
          this.clearCapturedForteResponses();
        }
      }
      // FORTE2 — independent branch (additive; mirrors the Payrix block, no else linkage).
      // ccProcessed was already flipped true by the forte2 success watcher BEFORE this save,
      // so the :298 unprocessed-CC guard is a no-op here without touching it.
      if (this.odSettingItem.selectedGateway === 'forte2' && this.forte2SuccessFlag) {
        // Each charged card/eCheck was captured as it succeeded (deduped by transactionId). The
        // forte2Payment singleton only holds the LAST charge, so build one taxccpayments record
        // per captured charge — otherwise a payment split across cards/eChecks gets only one CC
        // Payments Transaction row. Falls back to the single live singleton when nothing was
        // captured (backward-compatible with the existing single-charge path + unit tests).
        const capturedForte2Payments = this.forte2Payment.capturedForte2Payments || [];
        const forte2PosReqs = capturedForte2Payments.length
          ? capturedForte2Payments.map((snap) => this.forte2PosReqMapping(
            uptItem, returnedRes.paymentNumber, this.forte2Payment, snap,
          ))
          : [this.forte2PosReqMapping(uptItem, returnedRes.paymentNumber, this.forte2Payment)];
        // taxDetails are identical for every charge on the payment, so guarding the first
        // record covers them all.
        const firstForte2PosReq = forte2PosReqs.length ? forte2PosReqs[0] : {};
        if (!firstForte2PosReq.taxDetails || !firstForte2PosReq.taxDetails.length) {
          console.error('[Forte2] taxccpayments aborted: taxDetails is empty. uptItem:', JSON.stringify(uptItem));
          this.resetForte2Payment();
          this.disableSave = false;
          return {};
        }
        let allForte2CCSaved = forte2PosReqs.length > 0;
        for (let i = 0; i < forte2PosReqs.length; i += 1) {
          try {
            // Sequential (not Promise.all): upsertCCPaymentsTrans does a read-modify-write of
            // the CC transaction crud list, so parallel writes would clobber each other's rows.
            // eslint-disable-next-line no-await-in-loop
            await this.upsertCCPaymentsTrans({
              item: forte2PosReqs[i],
              baseCrudKey: 'ccPaymentTransaction',
            });
          } catch (ccPaymentError) {
            allForte2CCSaved = false;
            console.error('[Forte2] upsertCCPaymentsTrans failed for payment #', returnedRes.paymentNumber, ccPaymentError);
            this.flashError(
              `Payment #${returnedRes.paymentNumber} was saved successfully, but a credit card transaction record failed to save. `
              + 'Please contact support to manually create the CC payment record.',
            );
          }
        }
        // Full reset clears the flag, active row index, and singleton result (mirrors the prior
        // behavior). Drop the captured charges only once every record persisted; keep them for
        // manual recovery otherwise (a fresh payment's first charge clears them regardless).
        this.resetForte2Payment();
        if (allForte2CCSaved && capturedForte2Payments.length) {
          this.clearCapturedForte2Payments();
        }
      }
      // Keyed / "phone" credit payments are marked processed but never complete a
      // live Forte/Payrix/Forte2 charge, so the branches above skip them and no
      // taxCCPayments record is created -> they never reach the Credit Card Summary.
      // Create the record here so it is instantaneous (no manual reconcile needed).
      // The charged branches above own the swipe flow, so this only fills the
      // otherwise-empty path.
      if (returnedRes && returnedRes.paymentNumber
        && hasUnchargedProcessedCredit(uptItem.paymentDetails)
        && !this.payrixSuccessFlag
        && !this.forteResponse.fortePaymentSuscessFlag
        && !this.forte2SuccessFlag) {
        const manualCCReq = buildManualCCRecord(uptItem, returnedRes.paymentNumber, this.todaysDate);
        if (manualCCReq.taxDetails.length) {
          try {
            await this.upsertCCPaymentsTrans({
              item: manualCCReq,
              baseCrudKey: 'ccPaymentTransaction',
            });
          } catch (err) {
            console.error('[Keyed CC] manual taxCCPayments create failed:', err);
            this.custumColrFlashMsg({ msg: 'Payment saved, but the credit card record for the report failed to save. Please contact support.', color: 'red' });
          }
        }
      }
      this.disableSave = false;
      // to show audit dialog
      const auditSummaryArray = filtedItems.map((el) => el.auditSummary);
      const receiptNumberArray = returnedRes.taxDetails.map((el) => el.reciept);
      const auditItems = [];
      if (returnedRes.auditRecordsForVoid && returnedRes.auditRecordsForVoid._id) {
        // for showing voided audits
        const voidItem = returnedRes.auditRecordsForVoid;
        voidItem.businessdate = toShortDate(voidItem.businessdate);
        const voidedDetails = (returnedRes.taxDetails || []);
        voidItem.auditSummary = voidedDetails.map(
          (el) => `taxYr ${el.taxYear} TaxID ${el.taxId} Receipt# ${el.reciept} was voided`,
        ).join('\n');
        this.setItem(['paymentAuditRecordForVoid', voidItem]);
        this.setIsVisible(['paymentAuditRecordForVoid', true]);
      }

      if (filtedItems.length) {
        const businessdate = item.payDate ? toIsoDate(item.payDate) : null;
        auditSummaryArray.forEach((audit, i) => {
          const itemObj = {
            paymentNumberAudit: (returnedRes.paymentNumber),
            workstation: amountToNumber(item.workstation),
            reciept: String(receiptNumberArray[i] || ''),
            auditSummary: audit,
            businessdate,
            approvalStatus: false,
            approvalDate: null,
            approvalUser: null,
          };
          auditItems.push(itemObj);
        });

        // eslint-disable-next-line no-unreachable
        const auditRes = await this.upsertTaxPaymentAudits({
          item: auditItems,
        });
        if (auditRes.length) {
          const firstObj = auditRes[0];
          const allSummaryArray = auditRes.map((ft) => ft.auditSummary);
          const returnItemObj = {
            _id: firstObj._id,
            paymentNumberAudit: firstObj.paymentNumberAudit,
            workstation: firstObj.workstation,
            reciept: firstObj.reciept,
            auditSummary: allSummaryArray.join(),
            businessdate: firstObj.businessdate,
            user: firstObj.user,
          };

          this.setItems(['paymentAuditRecordData', auditRes] || []);
          this.setItem(['paymentAuditRecord', returnItemObj] || {});
          this.setIsVisible(['paymentAuditRecord', true]);
        }
      }

      // for save and print
      if (isItFromSaveNPrint && returnedRes._id) {
        const paymentId = returnedRes._id;
        const { jwt } = await this.reAuth();
        const params = [
          `paymentId=${paymentId}`,
          `token=${jwt}`,
        ];
        if (this.devSetting && this.devSetting.isTestEnvironment) {
          params.push('isTestCafe=1');
        }
        const queryString = generateQueryString(params);
        const url = `${backendRest.defaults.baseURL}/taxpayment/print-paid-receipt${queryString}`;
        // window.open(url, '_blank');
        printerHandler(this, { url, reportName: 'paidReceipts', deafultPrinterMode: true });
      }
      if ((this.mtgCodeItems || []).length && shouldCloseDialog
      && this.paymentSettingItem.warnWhenPayingRecWithMtg) {
        // need to check from settings as well
        this.popUpMtgCodeRemoveOrNot = true;
      }
      // await this.callMakeGLobalPaymentNumberUndefined({ criteria: {} });
      this.loadPaymentsWkstList(); // to reload wkst #
      // if picklist dialog is visible then we need to show empty picklist dialog
      if (shouldCloseDialog && this.isVisiblePIcklistDialog) {
        this.setEmptyPickListFlag(true);
      }

      this.setPaymentShotCutStore({ res: 'reset' });
      this.setHalfOrPostmark({ res: this.halfOrPostmark.half, whichOne: 'dialogHalf' });
      this.setHalfOrPostmark({ res: this.halfOrPostmark.postmark, whichOne: 'dialogPostmark' });
      if (shouldCloseDialog) {
        this.paymentReRenderFlag = false;
        this.onPaymentDialogClose();
      }
      return returnedRes || {};
    },

    // forteResponseOverride lets a caller build a record for ONE specific charge; without it
    // this falls back to the live singleton (ForteService.forteResponse), which only ever
    // holds the LAST charged card. Multi-card saves pass each captured response explicitly.
    fortePosReqMapping(upsertItem, paymentNo, ForteService, forteResponseOverride) {
      const { taxDetails } = upsertItem;
      const forteResp = forteResponseOverride || ForteService.forteResponse || {};
      const getCreditCardPayment = (upsertItem.paymentDetails || []).filter((ft) => ft.paymentType === 'CREDIT' && ft.amount === Number(forteResp.subtotal_amount));
      // Fall back to an empty row rather than indexing [0] blind: a response whose subtotal
      // matches no row would otherwise throw and abort the whole multi-card loop.
      const creditRow = getCreditCardPayment.length ? getCreditCardPayment[0] : {};
      const getCreditCardType = creditRow.creditType || '';
      const fortePosReq = {};
      fortePosReq.paymentGateway = 'FORTE';
      fortePosReq.paymentNo = paymentNo;
      fortePosReq.systemDate = upsertItem.payDate || toShortDate(this.todaysDate);
      fortePosReq.paymentDate = this.todaysDate;
      fortePosReq.merchantId = ForteService.forteConfig.locationId || '';
      fortePosReq.lengthOfTaxDetails = Array.isArray(upsertItem.taxDetails)
        ? upsertItem.taxDetails.length : 0;
      fortePosReq.transactionId = forteResp.trace_number || '';
      fortePosReq.amountPaid = creditRow.amount || 0;
      fortePosReq.processingFee = Number(forteResp.service_fee_amount) || 0;
      fortePosReq.maskedAccount = null;
      fortePosReq.status = 'OUTSTANDING';
      fortePosReq.modeOfPayment = 'POS';
      fortePosReq.apiLoginKey = '';
      fortePosReq.timeChanged = '';
      fortePosReq.paidBy = creditRow.paidBy || '';
      fortePosReq.emailAddress = forteResp.billing_email_address || '';
      fortePosReq.address1 = forteResp.billing_street_line1 || '';
      fortePosReq.address2 = forteResp.billing_street_line2 || '';
      fortePosReq.city = forteResp.billing_locality || '';
      fortePosReq.state = forteResp.billing_region || '';
      // sanitizeZipCode already strips the dash zipDeFormat handled, and unlike amountToNumber
      // it cannot emit a float — Forte returning "466.37" here used to persist a float that the
      // GraphQLInt zipCode field then refused to serialize (McCurtain, Jul 2026).
      fortePosReq.zipCode = sanitizeZipCode(forteResp.billing_postal_code);
      fortePosReq.phoneNo = forteResp.billing_phone_number || null;
      fortePosReq.chunkID = '';
      fortePosReq.isTempRecord = false;
      fortePosReq.paymentType = 'CREDIT';
      // specialPaymentType is a strict GraphQL+Mongoose enum: a valid value or absent
      // only ('' / null / unknown brand all fail to save). Normalize + omit when invalid.
      const fortePosReqSpecialPaymentType = normalizeSpecialPaymentType(getCreditCardType);
      if (fortePosReqSpecialPaymentType) {
        fortePosReq.specialPaymentType = fortePosReqSpecialPaymentType;
      }
      fortePosReq.taxDetails = upsertItem.taxDetails.map((taxDetail) => {
        const taxDetailItem = {
          taxYear: taxDetail.taxYear,
          taxId: taxDetail.taxId,
          paymentInstallment: 'FINAL', // TODO: test this
          detailsCount: taxDetails.length,
          taxAmount: taxDetail.taxAmt,
          penalty: taxDetail.penaltyAmt,
          feeTotal: taxDetail.totalFees,
          total: taxDetail.total,
        };
        return taxDetailItem;
      });
      return fortePosReq;
    },
    // forte2Override lets a caller build a record for ONE specific charge; without it this reads
    // the live singleton (this.forte2Payment), which only ever holds the LAST charge. Multi-card
    // saves pass each captured snapshot explicitly. Per-card fields (tender, principal, txn id,
    // fee, brand, cardholder, contact) come from `src`; config/date stay from `this`.
    forte2PosReqMapping(upsertItem, paymentNo, forte2, forte2Override) {
      const { taxDetails } = upsertItem;
      const src = forte2Override || forte2;
      // Match the charged row (card or eCheck) by its principal (the dialog captured this.subtotal,
      // i.e. the same paymentDetails row amount). amountPaid therefore comes from the taxpayments
      // principal — NOT the (subtotal+fee) authorization amount — mirroring fortePosReqMapping.
      const getCreditCardPayment = (upsertItem.paymentDetails || []).filter(
        (ft) => ft.paymentType === src.forte2PaymentType && ft.amount === Number(src.principalAmount),
      );
      const creditRow = getCreditCardPayment.length ? getCreditCardPayment[0] : {};
      const forte2PosReq = {};
      forte2PosReq.paymentGateway = 'FORTE2';
      forte2PosReq.paymentNo = paymentNo;
      forte2PosReq.systemDate = upsertItem.payDate || toShortDate(this.todaysDate);
      forte2PosReq.paymentDate = this.todaysDate;
      forte2PosReq.merchantId = (this.odSettingItem && this.odSettingItem.forte2LocId) || '';
      forte2PosReq.lengthOfTaxDetails = Array.isArray(upsertItem.taxDetails)
        ? upsertItem.taxDetails.length : 0;
      forte2PosReq.transactionId = src.transactionId || '';
      forte2PosReq.amountPaid = creditRow.amount || src.principalAmount || 0;
      forte2PosReq.processingFee = Number(src.processingFee) || 0;
      forte2PosReq.maskedAccount = null;
      forte2PosReq.status = 'OUTSTANDING';
      forte2PosReq.modeOfPayment = 'POS';
      forte2PosReq.apiLoginKey = '';
      forte2PosReq.timeChanged = '';
      forte2PosReq.paidBy = creditRow.paidBy || src.cardholderName || '';
      forte2PosReq.emailAddress = src.email || '';
      // The Forte2 dialog collects no billing address; the /forte2/process response echoes only
      // first/last name. Street/city/state/zip have no source — fall back to ''/null (Forte1 parity).
      forte2PosReq.address1 = '';
      forte2PosReq.address2 = '';
      forte2PosReq.city = '';
      forte2PosReq.state = '';
      forte2PosReq.zipCode = null;
      forte2PosReq.phoneNo = src.phone || null;
      forte2PosReq.chunkID = '';
      forte2PosReq.isTempRecord = false;
      forte2PosReq.paymentType = src.forte2PaymentType;
      // GraphQL enums travel by their KEY (name), not their server-side value. The backend
      // TaxCCSpecialPaymentTypeEnum expects keys like VISA_FORTE2, MASTERCARD_FORTE2, etc.
      // Card tender derives the key from the Forte card_type brand (visa/mast/amex/disc);
      // eCheck routes straight to ECHECK_FORTE2, bypassing the brand mapper.
      forte2PosReq.specialPaymentType = src.forte2PaymentType === 'ECHECK'
        ? 'ECHECK_FORTE2'
        : mapForte2CardTypeToSpecialPaymentTypeKey(src.cardType);
      forte2PosReq.taxDetails = upsertItem.taxDetails.map((taxDetail) => {
        const taxDetailItem = {
          taxYear: taxDetail.taxYear,
          taxId: taxDetail.taxId,
          paymentInstallment: 'FINAL',
          detailsCount: taxDetails.length,
          taxAmount: taxDetail.taxAmt,
          penalty: taxDetail.penaltyAmt,
          feeTotal: taxDetail.totalFees,
          total: taxDetail.total,
        };
        return taxDetailItem;
      });
      return forte2PosReq;
    },
    upsertNOverPaymentsCommon(item) {
      const updatedItem = this.deleteFieldsNotRequireToUpsert(item);
      // get the records only which have taxId and taxYear
      const filUpdatedItem = (updatedItem.taxDetails || [])
        .filter((ft) => (!!ft.taxId && !!ft.taxYear));
      updatedItem.taxDetails = filUpdatedItem;
      updatedItem.paymentNumber = amountToNumber(updatedItem.paymentNumber);
      updatedItem.workstation = amountToNumber(updatedItem.workstation);
      const convertToIsoPayDate = updatedItem.payDate
        ? toIsoDate(updatedItem.payDate) : null;
      updatedItem.payDate = convertToIsoPayDate;
      let totalPayment = 0;
      let totalDue = 0;
      if ((updatedItem.paymentDetails || []).length > 0) {
        updatedItem.paymentDetails = updatedItem.paymentDetails.map(
          (element) => {
            const newUpdatedElement = this.buildPaymentItem(element);
            totalPayment += amountToNumber(element.amount);
            newUpdatedElement.paymentsModes = newUpdatedElement.paymentType;
            return {
              ...newUpdatedElement,
            };
          },
        );
      }
      const mtgCodes = [];
      if ((updatedItem.taxDetails || []).length > 0) {
        updatedItem.taxDetails = updatedItem.taxDetails.map((element) => {
          const el = element;
          // let penAmt = null;
          el.taxYear = amountToNumber(element.taxYear);
          // update the taxid as if they search from payment-dialog, it can be pid or tid
          el.taxId = element.itsNewRecord ? Number(element.originalTaxId || 0)
            : Number(element.taxId || 0);
          el.taxAmt = amountToNumber(el.taxAmt);
          el.total = amountToNumber(el.total);
          el.totalDue = amountToNumber(el.totalDue);
          el.total = amountToNumber(el.total);
          el.penaltyAmt = amountToNumber(el.penaltyAmt);
          el.totalFees = amountToNumber(el.totalFees);
          if (el.tempVariableVoid) {
            el.void = true;
          }
          // el.void = el.tempVariableVoid || false;
          if (this.paymentSettingItem.decalNumberWhenDoingPayments && !!element.mobileHome) {
            this.mobileHomeArr.push({
              _id: el.taxInquiryId,
              taxYear: el.taxYear,
              taxId: el.taxId,
              mhDecalDate: convertToIsoPayDate,
            });
          }
          if (el.mtgCode) {
            mtgCodes.push(
              {
                mtgCode: el.mtgCode,
                _id: el.taxInquiryId,
                taxYear: el.taxYear,
                taxId: el.taxId,
              },
            );
          }
          // only add to the total if the taxType is TAXPAYMENT
          if (el.taxType === 'TAXPAYMENT') {
            totalDue += el.total;
          }
          const itemAmtFormat = [];
          // formatting to decimals
          for (let i = 0; i < (el.fees || []).length; i += 1) {
            const selFeeRec = el.fees[i];
            selFeeRec.feeAmount = amountToNumber(selFeeRec.feeAmount);
            itemAmtFormat.push(selFeeRec);
          }
          el.fees = itemAmtFormat;
          delete el.penaltyStartDate;
          delete el.totalPaid;
          delete el.totalTax;
          delete el.disableHalf;
          delete el.taxTotalDue;
          delete el.initialPenaltyAmt;
          delete el.initialTaxYear;
          delete el.initialTaxId;
          delete el.initialTaxAmt;
          delete el.initialTotalFees;
          delete el.tempVariableVoid;
          delete el.initialFees;
          delete el.allFeesList;
          delete el.AIsVisible;
          delete el.key;
          delete el.penaltyChangedManually;
          delete el.thereIsAPrevUnptRec;
          delete el.isFromTaxRollOrOwnerSearch;
          delete el.isFromOtherSources;
          delete el.isFromProtestOrPicklist;
          delete el.allowBackTaxEdit;
          // isFromProtest is screen-only noise on an ordinary payment (the pick list and the
          // protest fund list set it just to word a warning), but on the refund/credit rows the
          // Protested Tax screen builds it is the only thing that tells the server to draw the
          // protest record down and resolve it. Keep it there, drop it everywhere else.
          if (!isProtestRefundOrCredit(el)) {
            delete el.isFromProtest;
          }
          delete el.mtgCode;
          delete el.taxInquiryId;
          delete el.originalTaxId;
          delete el.mobileHome;
          delete el.baseId;
          // display-only (Tax Type column): taxDetailsInputFields has no such field and the
          // server stamps it from the roll record on every write
          delete el.propertyTaxType;
          const taxDetailItem = { ...el };
          return {
            ...taxDetailItem,
          };
        });
      }
      delete updatedItem.isVoid;
      delete updatedItem.amount;
      delete updatedItem.penaltyDate;
      delete updatedItem.cashAmount;
      delete updatedItem.checkAmount;
      delete updatedItem.otherAmount;
      delete updatedItem.cashChange;
      delete updatedItem.curPenaltyDate;
      delete updatedItem.everyTransVoided;
      delete updatedItem.hasFiles;
      if (mtgCodes.length) {
        this.mtgCodeItems = mtgCodes;
      }
      return { updatedItem, totalDue, totalPayment };
    },
    deleteFieldsNotRequireToUpsert(item, isDeleteId = false) {
      const updatedItem = { ...item };
      if (isDeleteId) {
        delete updatedItem._id;
      }
      if ((updatedItem.taxDetails || []).length) {
        updatedItem.taxDetails = updatedItem.taxDetails.map((taxDetail) => {
          const { ...rest } = taxDetail;
          delete rest.Half;
          delete rest.auditSummary;
          delete rest.originalTaxAmt;
          delete rest.originalTaxAmount;
          delete rest.originalGrossAssessed;
          if (taxDetail.itsNewRecord) delete rest._id;
          return rest;
        });
      }
      delete updatedItem.checkAmount;
      delete updatedItem.otherAmount;
      delete updatedItem.change;
      delete updatedItem.total;
      return updatedItem;
    },
    buildPaymentItem(element) {
      if (element.paymentType) {
        const amount = amountToNumber(element.amount);
        const updatedElement = {
          paymentType: element.paymentType,
          paidBy: element.paidBy,
          isReconcilled: element.isReconcilled,
          amount,
        };
        if (element.paymentType === 'CASH') {
          // updatedElement.cashReceived = amountToNumber(element.cashReceived);
          // updatedElement.cashChange = amountToNumber(element.cashChange);
        }
        if (element.paymentType === 'CREDIT') {
          updatedElement.creditType = element.creditType;
          updatedElement.ccProcessed = element.ccProcessed;
          // if (element.ccProcessed === false) {
          //   window.alert('Credit card payments are not created on CCPayment !');
          // }
        }
        if (element.paymentType === 'CHECK') {
          updatedElement.checkNumber = element.checkNumber;
          // updatedElement.checkPaidBy = element.checkPaidBy;
          if (element.overpaymentAmt) {
            updatedElement.overpaymentAmt = element.overpaymentAmt;
          }
        }
        if (element.paymentType === 'ECHECK') {
          updatedElement.checkNumber = element.checkNumber;
          updatedElement.ccProcessed = element.ccProcessed;
        }
        if (element.paymentType === 'EFT') {
          updatedElement.eftType = element.eftType;
        }
        if (element.mtgCode) {
          updatedElement.mtgCode = element.mtgCode;
        }
        return { ...updatedElement };
      }
      return { ...element };
    },
    postProccessData(items) {
      const updatedItems = [];
      const taxStatus = getTaxYearStatus();
      // to get in crud-list
      for (let i = 0; i < items.length; i += 1) {
        const item = { ...items[i] };
        let checkAmount = 0;
        let cashAmount = 0;
        let otherAmount = 0;
        (item.paymentDetails || []).map((ft) => {
          if (ft.paymentType === 'CHECK') checkAmount += ft.amount;
          else if (ft.paymentType === 'CASH') cashAmount += ft.amount;
          else otherAmount += ft.amount;
          return [];
        });
        item.checkAmount = checkAmount;
        item.cashAmount = cashAmount;
        item.otherAmount = otherAmount;

        // Add asterisk to check amount if payment setting is enabled and no files are attached
        // check amount is formated to 2 decimal places, instead of in headres in CRUD
        if (this.paymentSettingItem && this.paymentSettingItem.highlightCheckPaymentsWithNoImage
          && checkAmount > 0
          && !item.hasFiles) {
          item.checkAmount = `${amountFormat(checkAmount)} *`;
        } else {
          item.checkAmount = `${amountFormat(checkAmount)}`;
        }
        // to convert to MM/DD/YYYY
        item.payDate = item.payDate ? toShortDate(item.payDate) : item.payDate;

        // to get cash-change
        const isThereCashPay = (item.paymentDetails || []).filter((ft) => ft.paymentType === 'CASH');
        const isThereCheckPay = (item.paymentDetails || []).filter((ft) => ft.paymentType === 'CHECK');
        const totalPaidMain = (item.taxDetails || []).reduce((acc, ft) => acc
          + amountToNumber(ft.total || 0), 0);
        const totalPaymentSum = (item.paymentDetails || []).reduce((acc, ft) => acc
          + amountToNumber(ft.amount || 0), 0);
        const isPaidGreaterThanTotalSum = (amountToNumber(totalPaymentSum))
          > (amountToNumber(totalPaidMain));
        if ((isThereCashPay.length
          || (this.paymentSettingItem.allowChangeDueOnCheckPayments && isThereCheckPay.length))
          && isPaidGreaterThanTotalSum) {
          const cashChange = amountFormat(amountToNumber(totalPaymentSum)
            - amountToNumber(totalPaidMain));
          item.cashChange = amountFormat(cashChange);
        }
        // if (item.paymentDetails && item.paymentDetails.length === 1
        // && item.paymentDetails[0].paymentType === 'CASH') {
        //   const total = item.taxDetails
        //     .reduce((acc, ft) => acc + amountToNumber(ft.total || 0), 0);
        //   const calcChange = item.paymentDetails[0].amount - total > 0
        //     ? item.paymentDetails[0].amount - total : 0;
        //   item.cashChange = amountFormat(calcChange);
        // }

        if (item.taxDetails && item.taxDetails.length > 0) {
          let voidValue;

          for (const taxDetail of item.taxDetails) {
            taxDetail.taxAmt = amountFormat(taxDetail.taxAmt);
            taxDetail.total = amountFormat(taxDetail.total);
            taxDetail.penaltyAmt = amountFormat(taxDetail.penaltyAmt);
            taxDetail.totalFees = amountFormat(taxDetail.totalFees);
            voidValue = taxDetail.void;
            if (taxDetail._id === null || taxDetail._id === undefined) {
              delete taxDetail._id;
            }
            // to disable tax amount for back taxes
            taxDetail.allowBackTaxEdit = false;
            if (amountToNumber(taxDetail.taxYear) <= amountToNumber(taxStatus.backTax)) {
              if (this.paymentSettingItem.allowPartialPayemnts) {
                taxDetail.allowBackTaxEdit = false;
              } else taxDetail.allowBackTaxEdit = true;
            }
          }
          // if every transcation is voided this will help us to show light yellow color
          const isEveryTransVoided = item.taxDetails.every((ft) => ft.void);

          item.everyTransVoided = isEveryTransVoided;
          item.total = totalPaidMain;
          item.void = voidValue;
        } else {
          item.taxDetails = [];
        }

        updatedItems.push(item);
      }
      return updatedItems;
    },
    // startTheCounter(item) {
    //   this.paymentNumberopolling = setInterval(() => {
    //     this.determineNextPaymentNumber(item);
    //   }, 1200);
    // },
    // determineNextPaymentNumber(item) {
    //   const selectItem = { ...item };
    //   const dialogIsOpenOrNot = this.isVisible(this.baseCrudKey);
    //   if (dialogIsOpenOrNot) {
    //     const fy = selectItem.fiscalYear
    //       ? selectItem.fiscalYear : toFiscalYear(new Date(this.todaysDate));
    //     const loadTaxPromise = this.generateNextPaymentNumber({
    //       fiscalYear: fy,
    //     });
    //     loadTaxPromise.then(() => {
    //       this.editedItem.paymentNumber = this.nextPaymentNumber;
    //     });
    //   } else clearInterval(this.paymentNumberopolling);
    // },
    onPaymentDialogClose() {
      this.setIsVisible([this.baseCrudKey, false]);
      this.setIsVisible(['pickListMakePaymentDialog', false]);
      this.setItem(['pickListMakePaymentDialog', {}]);
      this.setItem([this.baseCrudKey, {}]);
      this.setHalfOrPostmark({ res: this.halfOrPostmark.half, whichOne: 'dialogHalf' });
      this.setHalfOrPostmark({ res: this.halfOrPostmark.postmark, whichOne: 'dialogPostmark' });
      this.setPaymentShotCutStore({ res: 'reset' });
      this.setDisableOverUnderPay(false);
      this.setIsThereAnyUnpaids(false);
      this.setMisMatchItemsList([]);
      setTimeout(() => {
        this.paymentReRenderFlag = false;
        this.allMessageText = [];
        this.entireListErrorMessageText = [];
        this.showOrHidePopUpForAllMsg = false;
      }, 100);
    },
    // async loadMoreTaxRecords({ criteria }) {
    //   const res = await this.loadMoreTax({ criteria });

    //   return res;
    // },
    async upsertAuditTransaction(item) {
      const updatedauditRecordItem = { ...item };
      const convertToIsoBusinessdate = updatedauditRecordItem.businessdate
        ? toIsoDate(updatedauditRecordItem.businessdate) : null;
      updatedauditRecordItem.businessdate = convertToIsoBusinessdate;
      const itemData = this.items('paymentAuditRecordData');
      const updatedAuditItems = [];
      itemData.forEach((audit) => {
        const obj = {
          ...updatedauditRecordItem,
          auditSummary: audit.auditSummary,
          reciept: String(audit.reciept || ''),
        };
        updatedAuditItems.push(obj);
      });

      const auditCrudKey = 'paymentAuditTransactionList';
      await this.upsertTaxPaymentAudits({
        item: updatedAuditItems,
        baseCrudKey: auditCrudKey,
      });
    },
    async upsertAuditTransactionForVoids(item) {
      const updatedauditRecordItem = { ...item };
      const convertToIsoBusinessdate = updatedauditRecordItem.businessdate
        ? toIsoDate(updatedauditRecordItem.businessdate) : null;
      updatedauditRecordItem.businessdate = convertToIsoBusinessdate;
      const updatedAuditItems = [updatedauditRecordItem];
      await this.upsertTaxPaymentAudits({
        item: updatedAuditItems,
      });
    },
    async upsertCommonDecalDialogCrudKey() {
      const item = this.items('ComhDecalmmonDecalDialogCrudKey');
      // Only a row the clerk keyed a decal number on is an actual decal issue - the rows are built
      // with a decal date alone, and the backend writes nothing for those. Sending them anyway
      // came back "nothing modified" and flashed a save error at a clerk who never issued a decal.
      const decalsToSave = (item || []).filter((row) => String(row.mhDecal || '').trim());
      if (decalsToSave.length) {
        await this.upsertDecalDateAndDecalNumber({ item: decalsToSave });
      }
      this.setIsVisible(['ComhDecalmmonDecalDialogCrudKey', false]);
      this.setItems(['ComhDecalmmonDecalDialogCrudKey', []]);
      this.mobileHomeArr = [];
    },
  },
};
